'use strict';

import React, {useState, useMemo} from "react";
import moment from "moment";

import config from '../../shared/config';
import {apiCall} from "../../shared/api-client";
import {isStale, useStaleDataLoader} from "./state";
import {formatValue, formatNumber, addSummaries} from "./shared";
import {Months, LifeIntervals} from "./intervals";

const APIEndpoint = 'stats/dashboard/by-filter';
const GroupBy = 'environment';
const GeneratedImagesType = ['2DODFrame', 'Complete'];

const COLUMNS = [
	['This hour', 'hour0'],
	['Last hour', 'hour1'],
	['Last 24 Hours', 'hour24'],
	['Today', 'day0'],
	['Yesterday', 'day1'],
	['This month', 'month00'],
	['Last month', 'month01'],
	['Lifetime', 'life']
];

function DataTD({summary}) {
	if (!summary) {
		return <td data-empty="Interval summary was not in the api response"/>;
	}
	if (summary.stale) {
		return <td className="stale"/>;
	}
	let value = formatValue(summary, 'count', formatNumber);
	if (value === null) {
		return <td data-empty="Value is null"/>;
	}
	return <td>{value}</td>;
}

function SummaryTR({title, data}) {
	// Prepare data
	let {summaries} = data;
	if (!Array.isArray(summaries)) {
		return null;
	}
	let summariesByInterval = summaries.reduce(
		(o, s) => {
			let interval = s.interval.name;
			o[interval] = s;
			return o;
		}, {});


	summariesByInterval.life = LifeIntervals.reduce(
		(life, interval) => addSummaries(life, summariesByInterval[interval]),
		{count: 0, stale: false}
	);

	return <tr>
		<td>{title}</td>
		{
			COLUMNS.map(([t, i]) => <DataTD key={i} title={t} summary={summariesByInterval[i]}/>)
		}
	</tr>
}


function Usage2DTable({data}) {
	if (!data) {
		return <div className="loader">Loading 2d usage data</div>;
	}
	return <section>
		<h2>Generated 2D images summary, by service</h2>
		<table className="metrics-table cm-table table table-stripped usage-2d">
			<thead>
				<tr>
					<th>Service</th>
					{COLUMNS.map(([t, k]) => <th key={k}>{t}</th>)}
				</tr>
			</thead>
			<tbody>
				{
					Object.entries(data)
						.map(
							([service, d]) => <SummaryTR key={`mt-row-${service}`} title={service} data={d}/>
						)
				}
			</tbody>
		</table>
	</section>;
}

function MonthTR({month, data}) {
	let services = Object.keys(data);
	let monthFrom;
	let tds = [];
	for (let s of services) {
		let serviceData = data[s];
		let monthSummary;
		for (let summary of serviceData.summaries) {
			let interval = summary.interval;
			if (!interval || interval.name !== month) {
				continue;
			}
			monthSummary = summary;
			monthFrom = monthFrom || interval.from;
			break;
		}
		tds.push(<DataTD key={s} summary={monthSummary}/>);
	}
	return <tr>
		<td>{moment(monthFrom).utc().format('MMM YYYY')}</td>
		{tds}</tr>
}

function Usage2DByMonth({data}) {
	if (!data) {
		return null;
	}

	return <section>
		<h2>Generated 2D images, by month</h2>
		<table className="metrics-table cm-table table table-stripped usage-2d-by-month">
			<thead>
				<tr>
					<th>Month</th>
					{
						Object.keys(data).map(
							service => <th key={service}>{service}</th>
						)
					}
				</tr>
			</thead>
			<tbody>
				{
					Months.map(
						month => <MonthTR key={month} month={month} data={data}/>
					)
				}
			</tbody>
		</table>
	</section>;
}

function Usage2D() {
	const filter = config.get('CustomerInfo.Usage2DFilter', null);
	const [data, setData] = useState(null);
	const loader = useMemo(() => {
		return async (cancelled) => {
			let response = await apiCall({
				method: APIEndpoint,
				params: Object.assign({type: GeneratedImagesType, groupBy: GroupBy}, filter),
				applyConfiguredFilters: false
			});
			let data = response.data;
			// TODO: Remove it when API endpoint is implemented properly
			for (let [k, v] of Object.entries(data)) {
				let isEmpty = true;
				let {summaries} = v;
				if (summaries) {
					for (let s of summaries) {
						if (s && s.count) {
							isEmpty = false;
							break;
						}
					}
				}
				if (isEmpty) {
					delete data[k];
				}
			}
			if (!cancelled.current) {
				setData(data);
			}
			return isStale(data);
		};
	}, [setData, filter]);
	useStaleDataLoader(loader);


	return <section id="dashboard-usage-2d">
		<Usage2DTable data={data}/>
		<Usage2DByMonth data={data}/>
	</section>;
}

export default Usage2D;